import Role, { MODEL_STR, ALL } from "byzantine/src/Role";
import { formatNumber } from "@narmi/design_system";
import OrganizationAccessLevelExtraPermissions from "./OrganizationAccessLevelExtraPermissions";
import { bundleExtraPermissions } from "./OrganizationEditUser";

export const validateAccounts = (value: any[]) => {
  // at least one account must be selected for collaborators
  const hasSelectedOption = value.some((account) => account.selected);
  if (hasSelectedOption) return null;
  return "Must select at least one account";
};

// Helper to parse formatted currency strings (removes commas, etc.)
const parseLimitValue = (value: string): number => {
  // Remove formatting characters (commas, spaces, etc.) and parse
  const cleaned = value.replace(/[^\d.-]/g, "");
  return parseFloat(cleaned) || 0;
};

// Helper to safely extract limit amount from nested structure
// Handles both API format { "1": { amount: 5000 } } and internal format { 1: 5000 }
const getLimitAmount = (limits: any, limitType: string): number => {
  const limitEntry = limits?.[limitType];
  if (!limitEntry) return 0;
  
  const value = limitEntry["1"] || limitEntry[1];
  if (typeof value === "number") return value;
  if (typeof value === "object" && value?.amount) return value.amount;
  return 0;
};

export const getDefaultLimits = (features: Record<string, any> | undefined) => ({
  ach: features?.limits?.ach_push?.[1]
    ? formatNumber(features.limits.ach_push[1])
    : "",
  wire: features?.limits?.wire?.[1]
    ? formatNumber(features.limits.wire[1])
    : "",
  achPayment: features?.limits?.unverified_ach_push?.[1]
    ? formatNumber(features.limits.unverified_ach_push[1])
    : "",
  collectAchPayment: features?.limits?.unverified_ach_pull?.[1]
    ? formatNumber(features.limits.unverified_ach_pull[1])
    : "",
  fednowUnverifiedSend: features?.limits?.fednow_unverified?.[1]
    ? formatNumber(features.limits.fednow_unverified[1])
    : "",
  internationalWire: features?.limits?.international_wire?.[1]
    ? formatNumber(features.limits.international_wire[1])
    : "",
  achPassthroughSend: features?.limits?.ach_passthrough_send?.[1]
    ? formatNumber(features.limits.ach_passthrough_send[1])
    : "",
  achPassthroughCollect: features?.limits?.ach_passthrough_collect?.[1]
    ? formatNumber(features.limits.ach_passthrough_collect[1])
    : "",
});

interface RoleFormData {
  role_name: string;
  role_description: string;
  accessLevel: string;
  accounts: any[];
  externalAccounts: any[];
  extraPermissions: Record<string, boolean>;
  ach_limits: string;
  wire_limits: string;
  ach_payment_limits: string;
  collect_ach_payment_limits: string;
  fednow_unverified_send_limits: string;
  international_wire: string;
  ach_passthrough_send_limits: string;
  ach_passthrough_collect_limits: string;
  [MODEL_STR.ACCOUNT]?: boolean;
  [MODEL_STR.EXTERNAL_ACCOUNT]?: boolean;
  [key: string]: any;
}

export const buildInitialFormData = (options: {
  role?: Role | null;
  accounts: any[];
  externalAccounts: any[];
  defaultLimits: ReturnType<typeof getDefaultLimits>;
  isDuplicate?: boolean;
}): RoleFormData => {
  const { role, accounts, externalAccounts, defaultLimits, isDuplicate = false } = options;

  const initialData: RoleFormData = {
    // For duplicate flow, always start with empty name/description
    // For create/edit, pre-fill from role (if editing)
    role_name: isDuplicate ? "" : (role?.name ?? ""),
    role_description: isDuplicate ? "" : (role?.description ?? ""),
    accessLevel: role?.getAccessLevel() ?? "Collaborator",
    accounts: role
      ? role.markSelectedAccounts(accounts)
      : accounts,
    externalAccounts: role
      ? role.markSelectedAccounts(externalAccounts)
      : externalAccounts,
    extraPermissions: {},
    ach_limits: (() => {
      if (role?.limits) {
        const amount = getLimitAmount(role.limits, "ach_push");
        if (amount > 0) return formatNumber(amount.toString());
      }
      return defaultLimits.ach;
    })(),
    wire_limits: (() => {
      if (role?.limits) {
        const amount = getLimitAmount(role.limits, "wire");
        if (amount > 0) return formatNumber(amount.toString());
      }
      return defaultLimits.wire;
    })(),
    ach_payment_limits: (() => {
      if (role?.limits) {
        const amount = getLimitAmount(role.limits, "unverified_ach_push");
        if (amount > 0) return formatNumber(amount.toString());
      }
      return defaultLimits.achPayment;
    })(),
    collect_ach_payment_limits: (() => {
      if (role?.limits) {
        const amount = getLimitAmount(role.limits, "unverified_ach_pull");
        if (amount > 0) return formatNumber(amount.toString());
      }
      return defaultLimits.collectAchPayment;
    })(),
    fednow_unverified_send_limits: (() => {
      if (role?.limits) {
        const amount = getLimitAmount(role.limits, "fednow_unverified");
        if (amount > 0) return formatNumber(amount.toString());
      }
      return defaultLimits.fednowUnverifiedSend;
    })(),
    international_wire: (() => {
      if (role?.limits) {
        const amount = getLimitAmount(role.limits, "international_wire");
        if (amount > 0) return formatNumber(amount.toString());
      }
      return defaultLimits.internationalWire;
    })(),
    ach_passthrough_send_limits: (() => {
      if (role?.limits) {
        const amount = getLimitAmount(role.limits, "ach_passthrough_send");
        if (amount > 0) return formatNumber(amount.toString());
      }
      return defaultLimits.achPassthroughSend;
    })(),
    ach_passthrough_collect_limits: (() => {
      if (role?.limits) {
        const amount = getLimitAmount(role.limits, "ach_passthrough_collect");
        if (amount > 0) return formatNumber(amount.toString());
      }
      return defaultLimits.achPassthroughCollect;
    })(),
  };

  // Initialize extra permissions based on role
  OrganizationAccessLevelExtraPermissions.affectedPermissions.forEach(
    (model: string) => {
      const found = role
        ? role.permissions.find((p) => {
            const matchesModel = p.model_str === model;
            const isWriteOperation = ["*", "write"].includes(p.operation);
            if (
              !matchesModel ||
              (role.getAccessLevel() !== "Viewer" && !isWriteOperation)
            )
              return false;
            if (
              p.model_str !== MODEL_STR.EXTERNAL_ACCOUNT &&
              p.model_str !== MODEL_STR.ACCOUNT
            )
              return true;
            return p.uuid === ALL;
          })
        : undefined;

      initialData[model] = Boolean(found);
      initialData.extraPermissions[model] = Boolean(found);
    }
  );

  return initialData;
};

export const buildRoleFromFormData = (
  formData: RoleFormData,
  roles: Role[]
): {
  role: Role;
  limits: Record<string, any>;
} => {
  // Bundle extra permissions FIRST before checking them
  const extraPermissions = bundleExtraPermissions(formData) as Record<
    string,
    boolean
  >;

  const limits: Record<string, number> = {
    ach_limits: 0,
    wire_limits: 0,
    ach_payment_limits: 0,
    collect_ach_payment_limits: 0,
    fednow_unverified_send_limits: 0,
    international_wire_limits: 0,
    ach_passthrough_send_limits: 0,
    ach_passthrough_collect_limits: 0,
  };

  if (extraPermissions[MODEL_STR.ACH_TRANSFER]) {
    limits["ach_limits"] = parseLimitValue(formData.ach_limits);
  }
  if (
    extraPermissions[MODEL_STR.DOMESTIC_WIRE_TRANSACTION] ||
    extraPermissions[MODEL_STR.DOMESTIC_WIRE_VIA_TEMPLATES] ||
    extraPermissions[MODEL_STR.DOMESTIC_WIRE_IMPORT]
  ) {
    limits["wire_limits"] = parseLimitValue(formData.wire_limits);
  }
  if (
    extraPermissions[MODEL_STR.ACH_PAYMENT] ||
    extraPermissions[MODEL_STR.ACH_PAYMENT_VIA_TEMPLATE] ||
    extraPermissions[MODEL_STR.ACH_PAYMENT_VIA_IMPORT]
  ) {
    limits["ach_payment_limits"] = parseLimitValue(formData.ach_payment_limits);
  }
  if (
    extraPermissions[MODEL_STR.COLLECT_ACH_PAYMENT] ||
    extraPermissions[MODEL_STR.ACH_PAYMENT_COLLECT_VIA_TEMPLATE] ||
    extraPermissions[MODEL_STR.ACH_PAYMENT_COLLECT_VIA_IMPORT]
  ) {
    limits["collect_ach_payment_limits"] = parseLimitValue(
      formData.collect_ach_payment_limits
    );
  }
  if (extraPermissions[MODEL_STR.UNVERIFIED_FEDNOW_SEND]) {
    limits["fednow_unverified_send_limits"] = parseLimitValue(
      formData.fednow_unverified_send_limits
    );
  }
  if (
    extraPermissions[MODEL_STR.INTERNATIONAL_WIRE] ||
    extraPermissions[MODEL_STR.INTERNATIONAL_WIRE_VIA_TEMPLATES]
  ) {
    limits["international_wire_limits"] = parseLimitValue(formData.international_wire);
  }
  if (extraPermissions[MODEL_STR.ACH_PASSTHROUGH_SEND]) {
    limits["ach_passthrough_send_limits"] = parseLimitValue(
      formData.ach_passthrough_send_limits
    );
  }
  if (extraPermissions[MODEL_STR.ACH_PASSTHROUGH_COLLECT]) {
    limits["ach_passthrough_collect_limits"] = parseLimitValue(
      formData.ach_passthrough_collect_limits
    );
  }

  const generatedRole = Role.generateFromAccessLevelDeprecated(
    formData.accessLevel as "Admin" | "Collaborator" | "Viewer",
    formData.accounts,
    formData.externalAccounts,
    extraPermissions,
    roles,
    {
      ach_limits: limits["ach_limits"] || 0,
      wire_limits: limits["wire_limits"] || 0,
      ach_payment_limits: limits["ach_payment_limits"] || 0,
      collect_ach_payment_limits: limits["collect_ach_payment_limits"] || 0,
      international_wire_limits: limits["international_wire_limits"] || 0,
      fednow_unverified_send_limits: limits["fednow_unverified_send_limits"] || 0,
      ach_passthrough_send_limits: limits["ach_passthrough_send_limits"] || 0,
      ach_passthrough_collect_limits: limits["ach_passthrough_collect_limits"] || 0,
    } as API.Limits.ByType
  );

  const { limits: roleLimits } = generatedRole;

  return {
    role: generatedRole,
    limits: roleLimits,
  };
};
